import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SidebarComponent } from 'src/app/core/components/sidebar/sidebar.component';
import { AppUpdatePromptComponent } from './core/components/app-update-prompt/app-update-prompt.component';
import { HeaderComponent } from './core/components/header/header.component';
import { AbilityService } from './core/services/ability/ability.service';
import { AppService } from './core/services/app.service';
import { LogoutService } from './core/services/logout.service';
import { SessionService } from './core/services/session/session.service';
import { SsoBootstrapService } from './core/services/sso-bootstrap.service';
import { SsoScopeService } from './core/services/sso/sso-scope.service';
import { StorageBrowser } from './core/services/storage.service';
import { SwUpdateService } from './core/services/sw-update.service';
import { UserService } from './core/services/user.service';
import { AppSocketService } from './core/services/websocket.service';

const SSO_ALLOWED_URL = /^\/(working-area|reports|pdf-preview)(\/|\?|#|$)/;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    HeaderComponent,
    SidebarComponent,
    RouterModule,
    CommonModule,
    AppUpdatePromptComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  public noHeader: boolean;
  public currentUser?: any;
  private storageHandler: ((event: StorageEvent) => void) | null = null;
  private periodicCheckInterval: any;
  private userSub: any;

  constructor(
    private swUpdateService: SwUpdateService,
    private sessionService: SessionService,
    private appSocketService: AppSocketService,
    private appService: AppService,
    public router: Router,
    public storageBrowser: StorageBrowser,
    private userService: UserService,
    private ssoBootstrap: SsoBootstrapService,
    public ability: AbilityService,
    private ssoScope: SsoScopeService,
    private logoutService: LogoutService,
  ) {
    this.swUpdateService.initialize();
  }

  private currentUrl(): string {
    // router.url can be empty on cold start; location is reliable
    return (
      this.router.url || `${window.location.pathname}${window.location.search}`
    );
  }

  get showHeader(): boolean {
    const url = this.router.url;
    return !this.sessionService.isPublicRoute(url);
  }

  ngOnInit() {
    // Must run before the storage reads below so that an SSO landing
    // (`#access_token=...`) populates `token` before the rest of init reads it.
    this.ssoBootstrap.bootstrap();

    const url = this.currentUrl();
    const isPublic = this.sessionService.isPublicRoute(url);

    const token = this.storageBrowser.get('token');
    if (token) {
      this.appSocketService.connect();
    }
    const user = this.storageBrowser.get('user');
    if (user) {
      this.userService.currentUser.next(user);

      if (!isPublic) {
        if (this.sessionService.hasSessionExpired()) {
          this.appService.logout(
            'Timeout: You have been logged out due to inactivity.',
          );
          return;
        }

        // Since the user just opened/refreshed the tab, they are by definition "Active".
        this.storageBrowser.set('last_active', Date.now());
        this.sessionService.startIdleService();
      }
    }

    this.userSub = this.userService.currentUser.subscribe((currentUser) => {
      this.currentUser = currentUser;
    });

    // SSO self-heal on initial load:
    //   - URL bar typing of a sub-lab non-allowed URL (e.g. `sub-lab.com/`)
    //     during an active SSO session → redirect back into scope.
    //   - Browser back into a CST-re-execution-broken state (BE 302s to `/`
    //     after a consumed CST) → same code path, lands user back in scope.
    //   - Tab close + reopen of sub-lab URL where sessionStorage is gone
    //     but localStorage `user.isSSO` is still true → recover by going
    //     home (we have no entryUrl to redirect to).
    // Scope self-heal only applies to CST-style kiosk sessions
    // (isSSO AND mainLabUrl). Full SSO logins (mainLabUrl null) are regular
    // users authenticated via SSO — they have no bounded scope to heal to.
    if (this.ability.isSSO && this.ability.mainLabUrl) {
      const currentUrl = this.currentUrl();
      const allowed = SSO_ALLOWED_URL.test(currentUrl);
      if (!allowed) {
        const entryUrl = this.ssoScope.get()?.entryUrl;
        const mainLabUrl = this.ability.mainLabUrl;
        if (entryUrl && entryUrl !== currentUrl) {
          // Common case: sso_scope present, redirect back into scope.
          this.router.navigateByUrl(entryUrl);
        } else if (mainLabUrl) {
          // Edge case: sessionStorage lost (tab reopen) but localStorage
          // still flags this as an SSO user. We have no entry URL to honor
          // the scope, so end the session and send them home.
          this.logoutService.logoutAndRedirect(mainLabUrl);
        }
        // If neither entryUrl nor mainLabUrl is available, fall through:
        // there's nothing reasonable to do. The next normal redirect
        // (`/` → `/auth/login`) will run and the user can re-auth.
      }
    }

    // In-app popstate (browser back/forward from within the SPA) during SSO
    // that escapes the allowed scope → end SSO and return to main lab.
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((event) => {
        if (!this.ability.isSSO) return;
        if (event.navigationTrigger !== 'popstate') return;
        if (SSO_ALLOWED_URL.test(event.url)) return;
        const mainLabUrl = this.ability.mainLabUrl;
        if (mainLabUrl) {
          this.logoutService.logoutAndRedirect(mainLabUrl);
        }
      });

    // Listen for logout from another tab
    this.storageHandler = (event: StorageEvent) => {
      if (this.sessionService.isPublicRoute(this.currentUrl())) return;
      if (
        (event.key === 'token' && !event.newValue) ||
        (event.key === 'user' && !event.newValue)
      ) {
        this.appService.logout('Logged out from another tab!');
      }
    };
    window.addEventListener('storage', this.storageHandler as any);
    // Periodic check in case the storage event is missed
    this.periodicCheckInterval = setInterval(() => {
      if (this.sessionService.isPublicRoute(this.currentUrl())) return;

      const token = localStorage.getItem('token');
      console.log('Checking token and user...');
      if (!token && this.router.url !== '/auth/login') {
        this.appService.logout('User expired or removed, logging out!');
      }
    }, 60 * 1000);
  }

  ngOnDestroy() {
    // Cleanup event listeners and intervals
    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler as any);
      this.storageHandler = null;
    }
    if (this.periodicCheckInterval) {
      clearInterval(this.periodicCheckInterval);
      this.periodicCheckInterval = undefined;
    }
    if (this.userSub && this.userSub.unsubscribe) {
      this.userSub.unsubscribe();
      this.userSub = null;
    }

    // Let SW service cleanup
    try {
      this.swUpdateService.destroy();
    } catch (e) {}

    this.appSocketService.disconnect();
  }
}
